import { Injectable, OnDestroy, inject } from "@angular/core";
import { Subject } from "rxjs";
import { filter, distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { AgentKitService } from "../../../agent-ui";
import { ViewerConfigurationService } from "../../../viewer-components/services/viewer-configuration.service";

/**
 * Bridges the viewer authentication lifecycle with the CopilotKit runtime.
 *
 * Subscribes to ViewerConfigurationService and forwards the Bearer token into
 * AgentKitService.updateRuntime() whenever it changes (login, token refresh).
 * Because viewerConfiguration$ is a BehaviorSubject, this service immediately
 * receives the current token on creation — even if the user already logged in
 * before the first chat component mounted.
 *
 * Provided at root so it is a single instance, but it is created lazily:
 * ChatContainerComponent injects it to guarantee initialization before the
 * first CopilotKit call.
 */
@Injectable({ providedIn: "root" })
export class AgentKitRuntimeService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor() {
    const agentKit = inject(AgentKitService);
    const viewerConfig = inject(ViewerConfigurationService);

    viewerConfig.viewerConfiguration$
      .pipe(
        filter((config) => !!config?.token),
        map((config) => ({ Authorization: `Bearer ${config.token}` }) as Record<string, string>),
        distinctUntilChanged((a, b) => a.Authorization === b.Authorization),
        takeUntil(this.destroy$),
      )
      .subscribe((headers) => {
        agentKit.updateRuntime({ headers });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
